import { StyleDeclarationValue } from 'aphrodite';
import { Provider, observer } from 'mobx-react';
import * as React from 'react';
import { NavLink, useHistory, useParams } from 'react-router-dom';
import { useImpersonationContext } from '../../../../hooks/useImpersonationContext';
import { IImpersonationContextComponentProps } from '../../../../models';
import { MastheadPortalId } from '../../../../models/AdminModels';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { useAdminAccountQuery } from '../../../../queries';
import { ImpersonationContextViewModel } from '../../../../viewmodels/AdminViewModels';
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from '../../../../web/components/Breadcrumb';
import { MultiContainerHeader } from '../../../../web/components/MultiContainerHeader';
import { CreateAutomationTemplatePage } from '../../../../web/containers/CreateAutomationTemplate';
import { INavigationItemProps } from '../../../../web/containers/MainContainer';

interface IProps extends INavigationItemProps {
	className?: string;
	styles?: StyleDeclarationValue[];
}

const CreateAccountAutomationTemplateFc: React.FC<IProps> = props => {
	const history = useHistory();
	const params = useParams<{ id?: string }>();
	const userSession = useUserSession();
	const impersonationContext = useImpersonationContext({ accountId: params.id });
	const mImpersonationProviderProps: IImpersonationContextComponentProps = React.useRef({
		impersonationContext: new ImpersonationContextViewModel(params?.id ? { account: { id: params.id } } : undefined),
	}).current;
	const accountQuery = useAdminAccountQuery({ accountId: params.id });
	const companyName = accountQuery.data?.companyName;

	// Set global impersonation context on the user session
	React.useEffect(() => {
		if (impersonationContext && userSession) {
			userSession.impersonate(impersonationContext);
		}
		return () => {
			if (userSession) {
				userSession.impersonate(null);
			}
		};
	}, [impersonationContext, userSession]);

	const onRequestEdit = React.useCallback(
		(templateId: string) => {
			history.replace(`/accounts/${params.id}/automations/${templateId}/canvas`);
			return true;
		},
		[history, params]
	);

	return (
		<Provider {...mImpersonationProviderProps}>
			<MultiContainerHeader
				appBarHeader={
					<Breadcrumb>
						<BreadcrumbList>
							<BreadcrumbItem>
								<BreadcrumbLink asChild>
									<NavLink to='/accounts'>Accounts</NavLink>
								</BreadcrumbLink>
							</BreadcrumbItem>
							<BreadcrumbSeparator />
							<BreadcrumbItem>
								<BreadcrumbLink asChild>
									<NavLink to={`/accounts/${params.id}?tab=Automations`}>{companyName ?? 'Loading...'}</NavLink>
								</BreadcrumbLink>
							</BreadcrumbItem>
							<BreadcrumbSeparator />
							<BreadcrumbItem>
								<BreadcrumbPage>New Automation</BreadcrumbPage>
							</BreadcrumbItem>
						</BreadcrumbList>
					</Breadcrumb>
				}
				appBarPortalDestinationId={MastheadPortalId}
			/>
			<CreateAutomationTemplatePage {...props} impersonationAccountId={params?.id} onRequestEdit={onRequestEdit} />
		</Provider>
	);
};

export const CreateAccountAutomationTemplate = observer(CreateAccountAutomationTemplateFc);
